import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["itemsContainer", "item", "suggestionCard", "kbSuggestionsContainer"];

  addItem(event) {
    const template = document
      .getElementById("knowledge-base-item-template")
      .content.cloneNode(true);

    const newIndex = this.itemsContainerTarget.children.length;
    const newFields = template.querySelectorAll("[name]");

    newFields.forEach((field) => {
      field.name = field.name.replace("[]", `[${newIndex}]`);
    });

    this.itemsContainerTarget.appendChild(template);
  }

  addItemWithQuestion(event) {
    const button = event.currentTarget;
    const suggestedQuestion = button.dataset.question;
    const suggestionIndex = button.dataset.suggestionIndex;

    const template = document
      .getElementById("knowledge-base-item-template")
      .content.cloneNode(true);

    // Count existing items (each item is wrapped in a .contents div)
    const existingItems = this.itemTargets.length;
    const newIndex = existingItems;

    const newFields = template.querySelectorAll("[name]");

    newFields.forEach((field) => {
      field.name = field.name.replace("[]", `[${newIndex}]`);

      // Pre-fill the question field with the suggested question
      if (field.name.includes("[question]")) {
        field.value = suggestedQuestion;
      }
    });

    // Insert at the beginning of the container
    this.itemsContainerTarget.insertBefore(template, this.itemsContainerTarget.firstChild);

    // Find the newly added card and scroll to it
    const newCard = this.itemsContainerTarget.querySelector(".border.border-gray-200");
    if (newCard) {
      newCard.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });

      // Focus on the answer field since question is pre-filled
      const answerField = newCard.querySelector("[name*='[answer]']");
      if (answerField) {
        setTimeout(() => answerField.focus(), 300);
      }
    }

    // Hide the suggestion card that was just used
    if (suggestionIndex !== undefined && this.hasKbSuggestionsContainerTarget) {
      const suggestionCard = this.kbSuggestionsContainerTarget.querySelector(
        `[data-suggestion-index="${suggestionIndex}"]`
      );
      if (suggestionCard) {
        suggestionCard.style.display = "none";
      }
    }
  }

  async acceptAllSuggestions(event) {
    const button = event.currentTarget;
    const messageId = button.dataset.messageId;

    // Find all suggestion cards for this message
    const suggestionCards = this.suggestionCardTargets.filter(
      (card) => card.dataset.messageId === messageId
    );

    // Apply each suggestion
    suggestionCards.forEach((suggestionCard) => {
      const { itemIndex, fieldName, suggestedValue } = suggestionCard.dataset;

      // Find the input/textarea for this item and field
      const fieldSelector = `[name="support_agent_knowledge_base[knowledge_base_items_attributes][${itemIndex}][${fieldName}]"]`;
      const field = document.querySelector(fieldSelector);

      if (field) {
        field.value = suggestedValue;

        // Add a brief visual feedback to the field
        field.classList.add("animate-ring-flash");
      }

      // Gray out the suggestion card and add "Accepted" text
      suggestionCard.classList.add("opacity-50", "bg-gray-50");

      // Add "Accepted" text
      const textContainer = suggestionCard.querySelector(".text-gray-700");
      if (textContainer && !textContainer.querySelector(".accepted-text")) {
        const acceptedSpan = document.createElement("span");
        acceptedSpan.className = "accepted-text ml-2 text-green-600 font-semibold text-xs";
        acceptedSpan.textContent = "(Accepted)";
        textContainer.appendChild(acceptedSpan);
      }
    });

    // Find the message card
    const messageCard = button.closest("[id^='coach_bot_message_']");

    // Resolve the message on the backend
    const resolveUrl = button.dataset.resolveUrl;
    const resolved = await this.resolveMessage(resolveUrl);

    // Hide the entire message card after a delay
    if (resolved && messageCard) {
      setTimeout(() => {
        messageCard.remove();
      }, 1000);
    }
  }

  async dismissMessage(event) {
    const button = event.currentTarget;
    const dismissUrl = button.dataset.dismissUrl;

    // Find the message card to remove
    const messageCard = button.closest("[id^='coach_bot_message_']");

    // Resolve the message on the backend
    const resolved = await this.resolveMessage(dismissUrl);

    // Remove the message card from the DOM
    if (resolved && messageCard) {
      messageCard.remove();
    }
  }

  async resolveMessage(url) {
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content,
        },
      });

      return response.ok;
    } catch (error) {
      console.error("Error resolving message:", error);
      return false;
    }
  }

  removeItem(event) {
    const item = event.target.closest(
      "[data-support-agent--knowledge-base-items-field-target='item']"
    );

    // If the item has an ID, mark it for destruction instead of removing it from the DOM immediately
    const hiddenDestroyField = item.querySelector("[name*='[_destroy]']");
    if (hiddenDestroyField) {
      hiddenDestroyField.value = "1";
      item.style.display = "none"; // Hide instead of removing immediately
    } else {
      item.remove();
    }
  }
}
